import { useEffect, useState } from "react";
import { DEFAULT_SUBMISSION_SORT, type SubmissionFilter, type SubmissionSort } from "@sga/shared";
import { FilterBar } from "./FilterBar.js";
import { SubmissionsTable } from "./SubmissionsTable.js";
import { DetailDrawer } from "./DetailDrawer.js";
import { AssistedEntry } from "./AssistedEntry.js";
import { ExportDialog } from "../exports/ExportDialog.js";
import { Download, UserPlus } from "lucide-react";
import { useAuth } from "../auth/AuthContext.js";
import { PageHeader } from "../components/PageHeader.js";
import { Button } from "../components/Button.js";
import {
  useColumns,
  useDeleteView,
  useSaveView,
  useSavedViews,
  type SubmissionQueryState,
} from "./useSubmissions.js";

/**
 * The submissions screen: filter bar, virtualised table, detail drawer and saved views —
 * docs/01-PRODUCT-SPEC.md §9.1. The pieces are separate components because this file would
 * otherwise be well past the 200-line limit in CLAUDE.md, and because the table is the part that
 * has to stay fast enough to be profiled on its own.
 */
export const SubmissionsPage = ({
  formId,
  formTitle,
  initialFilters,
  onBack,
}: {
  formId: string;
  formTitle: string;
  /** Set when the table is opened from a chart — the category clicked, as a filter. */
  initialFilters?: SubmissionFilter[];
  onBack: () => void;
}): JSX.Element => {
  const [filters, setFilters] = useState<SubmissionFilter[]>(initialFilters ?? []);
  const [sort, setSort] = useState<SubmissionSort>(DEFAULT_SUBMISSION_SORT);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [viewId, setViewId] = useState<string | undefined>();
  const [openSubmissionId, setOpenSubmissionId] = useState<string | null>(null);
  const [selection, setSelection] = useState<Set<string>>(new Set());
  const [selectAllMatching, setSelectAllMatching] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [enteringAssisted, setEnteringAssisted] = useState(false);
  const { user } = useAuth();

  const columns = useColumns(formId);
  const views = useSavedViews(formId);
  const saveView = useSaveView(formId);
  const deleteView = useDeleteView(formId);

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(search), 250);
    return () => clearTimeout(handle);
  }, [search]);

  // A default view is applied once, on arrival. Re-applying it whenever the list refetches would
  // undo a filter the user had just changed.
  const [defaultApplied, setDefaultApplied] = useState(false);
  useEffect(() => {
    if (defaultApplied || !views.data) return;
    // Arriving from a chart means the user has already said what they want to see. A saved default
    // view would silently replace it with something else, which reads as the click not working.
    if (initialFilters && initialFilters.length > 0) {
      setDefaultApplied(true);
      return;
    }
    const preferred = views.data.data.find((view) => view.isDefault);
    if (preferred) {
      setViewId(preferred.id);
      setFilters(preferred.filters);
      setSort(preferred.sort);
    }
    setDefaultApplied(true);
  }, [views.data, defaultApplied, initialFilters]);

  const state: SubmissionQueryState = {
    filters,
    sort,
    q: debouncedSearch.trim().length >= 2 ? debouncedSearch.trim() : undefined,
    viewId,
    includeTest: false,
  };

  // Taking a registration for somebody who cannot fill the form in — at a venue, or on the phone.
  if (enteringAssisted) {
    return (
      <AssistedEntry
        formId={formId}
        formTitle={formTitle}
        onBack={() => setEnteringAssisted(false)}
      />
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      <PageHeader
        title={formTitle}
        back={{ label: "Forms", onClick: onBack }}
        actions={
          <>
            <Button icon={UserPlus} onClick={() => setEnteringAssisted(true)}>
              Enter a registration
            </Button>
            <Button icon={Download} onClick={() => setExporting(true)}>
              Export
            </Button>
          </>
        }
      />

      <FilterBar
        columns={columns.data?.data ?? []}
        filters={filters}
        onFiltersChange={(next) => {
          setFilters(next);
          // Changing the filter detaches from the saved view rather than silently editing it.
          setViewId(undefined);
          setSelectAllMatching(false);
        }}
        views={views.data?.data ?? []}
        activeViewId={viewId}
        onViewSelect={(id) => {
          setViewId(id);
          const view = views.data?.data.find((entry) => entry.id === id);
          setFilters(view?.filters ?? []);
          setSort(view?.sort ?? DEFAULT_SUBMISSION_SORT);
        }}
        onSaveView={(name, isShared, isDefault) =>
          saveView.mutate({
            name,
            columns: [],
            filters,
            sort,
            isShared,
            isDefault,
          })
        }
        onDeleteView={(id) => {
          deleteView.mutate(id);
          if (viewId === id) setViewId(undefined);
        }}
        search={search}
        onSearchChange={setSearch}
      />

      <SubmissionsTable
        formId={formId}
        state={state}
        onSortChange={setSort}
        onOpenDetail={setOpenSubmissionId}
        selection={selection}
        onSelectionChange={setSelection}
        selectAllMatching={selectAllMatching}
        onSelectAllMatching={setSelectAllMatching}
        onClearFilters={() => {
          setFilters([]);
          setSearch("");
          setViewId(undefined);
          setSelectAllMatching(false);
        }}
      />

      <DetailDrawer submissionId={openSubmissionId} onClose={() => setOpenSubmissionId(null)} />

      {exporting && (
        <ExportDialog
          formId={formId}
          columns={columns.data?.data ?? []}
          filters={filters}
          viewId={viewId}
          canViewPii={user?.canViewPii ?? false}
          isSuperadmin={user?.isSuperadmin ?? false}
          onClose={() => setExporting(false)}
        />
      )}
    </div>
  );
};
